"use client";
import { redirectAfterLoginIfNeeded } from "@/app/page/rootComponents/AuthHubListener";
import { useUpgradePopupContextSelector } from "@/features/Payments/PaywallPopup/UpgradePopupContext";
import { SubscriptionType, UpgradeEvent } from "@knowt/syncing/graphql/schema";
import GUIDED_EVENTS, { AllEventNames } from "@knowt/syncing/hooks/guidedEvents/guidedEvents";
import useGuidedEvent from "@knowt/syncing/hooks/guidedEvents/useGuidedEvent";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";

import { useCallback, useEffect, useRef } from "react";
import { SS_OFFERS_PROMO } from "../Auth/utils";
import { OfferTypes } from "../Payments/utils/constants";
import { fetchPostJSON } from "@knowt/syncing/fetchFunctions/fetchWrappers";
import { STUDENTS_PRICING_OPTIONS } from "@knowt/syncing/hooks/user/subscriptionConstants";
import getStripe from "@/utils/stripe/stripeJS";

const PlanPromotion = () => {
    const { user, subscriptionType } = useCurrentUser();
    const openUpgradePopup = useUpgradePopupContextSelector(state => state.openUpgradePopup);

    const { closeEvent, stagedEvents } = useGuidedEvent(GUIDED_EVENTS.PLAN_PROMOTION);

    const opened = useRef(false);

    const createUnidaysPromoCheckout = useCallback(
        async ({
            offerPromoCode,
            offerType,
        }: {
            offerPromoCode: string;
            offerType: OfferTypes;
        }) => {
            const response = await fetchPostJSON("/api/checkout_sessions", {
                priceId: STUDENTS_PRICING_OPTIONS.MONTHLY_LIMITLESS.priceId,
                email: user?.Email,
                code: offerPromoCode,
                ...(offerType === OfferTypes.UNIDAYS && { offerType }),
            });

            if (response.statusCode === 500) {
                return;
            }

            const stripe = await getStripe();
            await stripe?.redirectToCheckout({ sessionId: response.data.id });
        },
        [user?.Email]
    );

    useEffect(() => {
        if (!opened.current && stagedEvents?.length) {
            opened.current = true;

            const unidaysPromo = window.sessionStorage.getItem(SS_OFFERS_PROMO(OfferTypes.UNIDAYS));
            const studentBeansPromo = window.sessionStorage.getItem(SS_OFFERS_PROMO(OfferTypes.STUDENT_BEANS));

            if (subscriptionType === SubscriptionType.BASIC) {
                if (unidaysPromo || studentBeansPromo) {
                    window.sessionStorage.removeItem(SS_OFFERS_PROMO(OfferTypes.UNIDAYS));
                    window.sessionStorage.removeItem(SS_OFFERS_PROMO(OfferTypes.STUDENT_BEANS));
                    createUnidaysPromoCheckout({
                        offerPromoCode: (unidaysPromo || studentBeansPromo) as string,
                        offerType: unidaysPromo ? OfferTypes.UNIDAYS : OfferTypes.STUDENT_BEANS,
                    });

                    return;
                }

                openUpgradePopup({
                    event: UpgradeEvent.GENERIC,
                    isForPromotion: true,
                    context: {},
                    onClose: () => async () => {
                        await closeEvent(AllEventNames.STUDENT_TRIAL_2024);
                        redirectAfterLoginIfNeeded();
                    },
                    dialogProps: {
                        slotProps: {
                            backdrop: {
                                style: {
                                    background: "transparent",
                                },
                            },
                        },
                    },
                });
            } else {
                closeEvent(AllEventNames.STUDENT_TRIAL_2024).then(redirectAfterLoginIfNeeded);
            }
        }
    }, [closeEvent, openUpgradePopup, stagedEvents, subscriptionType, createUnidaysPromoCheckout]);

    return null;
};

export default PlanPromotion;
